import "core-js/modules/web.dom-collections.iterator.js";
/**
 * TourPortal.jsx
 * Framework for tour
 *
 * Not a real "portal" in the sense of a react portal, even though it
 * started out as wanting to be that. Our not-yet-invented "new modal system"
 * will probably utilise a react portal, though.
 *
 * TODO: loop through configparameters for ability to customise at runtime
 * , then add docs for customisation
 */
import { autorun } from "mobx";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, withTheme } from "styled-components";
import Box from "../../Styled/Box";
import Button from "../../Styled/Button";
import Spacing from "../../Styled/Spacing";
import Text from "../../Styled/Text";
import { parseCustomMarkdownToReactWithOptions } from "../Custom/parseCustomMarkdownToReact";
import Caret from "../Generic/Caret";
import CloseButton from "../Generic/CloseButton";
import { useWindowSize } from "../Hooks/useWindowSize";
import { useViewState } from "../Context";
import { applyTranslationIfExists } from "../../Language/languageHelpers";
import { calculateLeftPosition, calculateTopPosition, getOffsetsFromTourPoint } from "./tour-helpers.ts";
import TourExplanationBox, { TourExplanationBoxZIndex } from "./TourExplanationBox";
import TourIndicator from "./TourIndicator";
import TourOverlay from "./TourOverlay.jsx";
import TourPrefaceBox from "./TourPrefaceBox";
import TourProgressDot from "./TourProgressDot.jsx";

/**
 * Indicator bar/"dots" on progress of tour.
 * Fill in indicator dot depending on progress determined from count & max count
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const TourProgress = _ref => {
  let {
    max,
    step,
    setTourIndex
  } = _ref;
  const countArray = Array.from(Array(max).keys()).map(e => e++);
  const countStep = step;
  return /*#__PURE__*/_jsx(Box, {
    centered: true,
    children: countArray.map(count => {
      return /*#__PURE__*/_jsx(TourProgressDot, {
        onClick: () => setTourIndex(count),
        active: count < countStep
      }, count);
    })
  });
};
TourProgress.propTypes = {
  setTourIndex: PropTypes.func.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired
};
export const TourExplanation = _ref2 => {
  let {
    topStyle,
    leftStyle,
    caretOffsetTop,
    caretOffsetLeft,
    indicatorOffsetTop,
    indicatorOffsetLeft,
    setTourIndex,
    onTourIndicatorClick,
    onPrevious,
    onNext,
    onSkip,
    currentStep,
    maxSteps,
    active,
    isFirstTourPoint,
    isLastTourPoint,
    children
  } = _ref2;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  if (!active) {
    // Tour explanation requires the various positioning even if only just
    // showing the "tour indicator" button, as it is offset against the caret
    // which is offset against the original box
    return /*#__PURE__*/_jsx(Box, {
      position: "absolute",
      style: {
        zIndex: TourExplanationBoxZIndex - 1,
        top: topStyle,
        left: leftStyle
      },
      children: /*#__PURE__*/_jsx(Box, {
        position: "absolute",
        style: {
          top: "".concat(caretOffsetTop, "px"),
          left: "".concat(caretOffsetLeft, "px")
        },
        children: /*#__PURE__*/_jsx(TourIndicator, {
          onClick: onTourIndicatorClick,
          style: {
            top: "".concat(indicatorOffsetTop, "px"),
            left: "".concat(indicatorOffsetLeft, "px")
          }
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(TourExplanationBox, {
    paddedRatio: 3,
    column: true,
    style: {
      top: topStyle,
      left: leftStyle
    },
    children: [/*#__PURE__*/_jsx(CloseButton, {
      color: theme.darkWithOverlay,
      topRight: true,
      onClick: () => onSkip === null || onSkip === void 0 ? void 0 : onSkip()
    }), /*#__PURE__*/_jsx(Spacing, {
      bottom: 2
    }), /*#__PURE__*/_jsx(Caret, {
      style: {
        top: "".concat(caretOffsetTop, "px"),
        left: "".concat(caretOffsetLeft, "px")
      }
    }), /*#__PURE__*/_jsxs(Text, {
      light: true,
      medium: true,
      textDarker: true,
      children: [/*#__PURE__*/_jsx(Text, {
        light: true,
        medium: true,
        noFontSize: true,
        textDarker: true,
        children: children
      }), /*#__PURE__*/_jsx(Spacing, {
        bottom: 3
      }), /*#__PURE__*/_jsxs(Box, {
        centered: true,
        justifySpaceBetween: true,
        children: [/*#__PURE__*/_jsx(TourProgress, {
          setTourIndex: setTourIndex,
          step: currentStep,
          max: maxSteps
        }), /*#__PURE__*/_jsxs(Box, {
          centered: true,
          children: [!isFirstTourPoint && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Button, {
              secondary: true,
              shortMinHeight: true,
              onClick: () => onPrevious === null || onPrevious === void 0 ? void 0 : onPrevious(),
              children: t("general.back")
            }), /*#__PURE__*/_jsx(Spacing, {
              right: 2
            })]
          }), isLastTourPoint ? /*#__PURE__*/_jsx(Button, {
            primary: true,
            shortMinHeight: true,
            onClick: () => onSkip === null || onSkip === void 0 ? void 0 : onSkip(),
            children: t("tour.finish")
          }) : /*#__PURE__*/_jsx(Button, {
            primary: true,
            shortMinHeight: true,
            onClick: () => onNext === null || onNext === void 0 ? void 0 : onNext(),
            children: t("general.next")
          })]
        })]
      })]
    })]
  });
};
TourExplanation.propTypes = {
  children: PropTypes.node.isRequired,
  currentStep: PropTypes.number.isRequired,
  maxSteps: PropTypes.number.isRequired,
  caretOffsetTop: PropTypes.number,
  caretOffsetLeft: PropTypes.number,
  indicatorOffsetTop: PropTypes.number,
  indicatorOffsetLeft: PropTypes.number,
  setTourIndex: PropTypes.func.isRequired,
  onTourIndicatorClick: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  topStyle: PropTypes.string,
  leftStyle: PropTypes.string,
  isFirstTourPoint: PropTypes.bool.isRequired,
  isLastTourPoint: PropTypes.bool.isRequired,
  active: PropTypes.bool
};
const TourGrouping = observer(_ref3 => {
  var _currentTourPointRef$, _currentTourPointRef$2;
  let {
    tourPoints
  } = _ref3;
  const {
    i18n
  } = useTranslation();
  const viewState = useViewState();
  const currentTourPoint = tourPoints[viewState.currentTourIndex];
  const currentTourPointRef = viewState.appRefs.get(currentTourPoint === null || currentTourPoint === void 0 ? void 0 : currentTourPoint.appRefName);
  const currentRectangle = currentTourPointRef === null || currentTourPointRef === void 0 || (_currentTourPointRef$ = currentTourPointRef.current) === null || _currentTourPointRef$ === void 0 || (_currentTourPointRef$2 = _currentTourPointRef$.getBoundingClientRect) === null || _currentTourPointRef$2 === void 0 ? void 0 : _currentTourPointRef$2.call(_currentTourPointRef$);
  if (!currentRectangle) {
    console.log("Tried to show guidance portal with no rectangle available from ref");
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [currentRectangle && /*#__PURE__*/_jsx(TourOverlay, {
      rectangle: currentRectangle,
      onCancel: () => viewState.nextTourPoint()
    }), tourPoints.map((tourPoint, index) => {
      var _tourPointRef$current, _tourPointRef$current2;
      const tourPointRef = viewState.appRefs.get(tourPoint === null || tourPoint === void 0 ? void 0 : tourPoint.appRefName);
      const currentRectangle = tourPointRef === null || tourPointRef === void 0 || (_tourPointRef$current = tourPointRef.current) === null || _tourPointRef$current === void 0 || (_tourPointRef$current2 = _tourPointRef$current.getBoundingClientRect) === null || _tourPointRef$current2 === void 0 ? void 0 : _tourPointRef$current2.call(_tourPointRef$current);
      const {
        offsetTop,
        offsetLeft,
        caretOffsetTop,
        caretOffsetLeft,
        indicatorOffsetTop,
        indicatorOffsetLeft
      } = getOffsetsFromTourPoint(tourPoint);

      // To match old HelpScreenWindow / HelpOverlay API
      const currentScreen = {
        rectangle: currentRectangle,
        positionTop: (tourPoint === null || tourPoint === void 0 ? void 0 : tourPoint.positionTop) || viewState.relativePosition.RECT_BOTTOM,
        positionLeft: (tourPoint === null || tourPoint === void 0 ? void 0 : tourPoint.positionLeft) || viewState.relativePosition.RECT_LEFT,
        offsetTop: offsetTop,
        offsetLeft: offsetLeft
      };
      const positionLeft = calculateLeftPosition(currentScreen);
      const positionTop = calculateTopPosition(currentScreen);
      const currentTourIndex = viewState.currentTourIndex;
      const maxSteps = tourPoints.length;
      if (!tourPoint) return null;
      return /*#__PURE__*/_jsx(TourExplanation, {
        active: currentTourIndex === index,
        currentStep: currentTourIndex + 1,
        maxSteps: maxSteps,
        setTourIndex: idx => viewState.setTourIndex(idx),
        onTourIndicatorClick: () => viewState.setTourIndex(index),
        onPrevious: () => viewState.previousTourPoint(),
        onNext: () => viewState.nextTourPoint(),
        onSkip: () => viewState.closeTour(),
        isFirstTourPoint: index === 0,
        isLastTourPoint: index === tourPoints.length - 1,
        topStyle: "".concat(positionTop, "px"),
        leftStyle: "".concat(positionLeft, "px"),
        caretOffsetTop: caretOffsetTop,
        caretOffsetLeft: caretOffsetLeft,
        indicatorOffsetTop: indicatorOffsetTop,
        indicatorOffsetLeft: indicatorOffsetLeft,
        children: parseCustomMarkdownToReactWithOptions(applyTranslationIfExists(tourPoint === null || tourPoint === void 0 ? void 0 : tourPoint.content, i18n), {
          injectTermsAsTooltips: true,
          tooltipTerms: viewState.terria.configParameters.helpContentTerms
        })
      }, tourPoint.appRefName);
    })]
  });
});
export const TourPreface = () => {
  const {
    t
  } = useTranslation();
  const viewState = useViewState();
  const theme = useTheme();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TourPrefaceBox, {
      onClick: () => viewState.closeTour(),
      role: "presentation",
      "aria-hidden": "true",
      pseudoBg: true
    }), /*#__PURE__*/_jsxs(TourExplanationBox, {
      longer: true,
      paddedRatio: 4,
      column: true,
      style: {
        right: 25,
        bottom: 45
      },
      children: [/*#__PURE__*/_jsx(CloseButton, {
        color: theme.darkWithOverlay
        // color={"green"}
        ,
        topRight: true,
        onClick: () => viewState.closeTour()
      }), /*#__PURE__*/_jsx(Spacing, {
        bottom: 2
      }), /*#__PURE__*/_jsx(Text, {
        extraExtraLarge: true,
        bold: true,
        textDarker: true,
        children: t("tour.preface.title")
      }), /*#__PURE__*/_jsx(Spacing, {
        bottom: 3
      }), /*#__PURE__*/_jsx(Text, {
        light: true,
        medium: true,
        textDarker: true,
        children: t("tour.preface.content")
      }), /*#__PURE__*/_jsx(Spacing, {
        bottom: 4
      }), /*#__PURE__*/_jsx(Text, {
        medium: true,
        children: /*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsx(Button, {
            fullWidth: true,
            secondary: true,
            onClick: e => {
              e.stopPropagation();
              viewState.closeTour();
            },
            children: t("tour.preface.close")
          }), /*#__PURE__*/_jsx(Spacing, {
            right: 3
          }), /*#__PURE__*/_jsx(Button, {
            primary: true,
            fullWidth: true,
            textProps: {
              noFontSize: true
            },
            onClick: e => {
              e.stopPropagation();
              viewState.setShowTour(true);
            },
            children: t("tour.preface.start")
          })]
        })
      }), /*#__PURE__*/_jsx(Spacing, {
        bottom: 1
      })]
    })]
  });
};
export const TourPortalDisplayName = "TourPortal";
export const TourPortal = observer(() => {
  const viewState = useViewState();
  const showPortal = viewState.currentTourIndex !== -1;
  const showPreface = showPortal && !viewState.showTour;
  // should we bump up the debounce here? feels like 16ms is quite aggressive
  // and almost to the point of not debouncing at all, but the render logic
  // is quite cheap so it makes for a better resizing/zooming experience
  const width = useWindowSize({
    debounceOverride: 16
  });
  useEffect(() => autorun(() => {
    if (showPortal && viewState.topElement !== TourPortalDisplayName) {
      viewState.setTopElement(TourPortalDisplayName);
    }
  }));
  if (viewState.useSmallScreenInterface || !showPortal) {
    return null;
  }
  if (showPreface) {
    return /*#__PURE__*/_jsx(TourPreface, {
      viewState: viewState
    });
  }
  return /*#__PURE__*/_jsx(TourGrouping, {
    viewState: viewState,
    tourPoints: viewState.tourPointsWithValidRefs
  }, width);
});
TourPortal.propTypes = {
  children: PropTypes.node
};
export default withTheme(TourPortal);